import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { GRAY_700, GRAY_900, RhetorikFontFamily } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { ActionPage } from 'components/common/ActionPage/ActionPage.component'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import Logo from 'components/common/Logo/Logo'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIInputField } from 'components/common/MUIInputField'
import { Path } from 'constants/router'
import { SubscriptionPlan } from 'models/user'
import { SystemMessage } from 'components/common/SystemMessage'
import { logger } from 'utils/logging'
import { EntityKeys, QueryType } from 'utils/reactQuery/queryTypes'
import { getWorkspaceMembershipFromCache, pollUserMeUntilWorkspaceMember } from 'utils/helpers/pollUserMeUntilWorkspaceMember'
import { useCreateWorkspace } from 'dataQueries/workspace.query'
import { useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useRememberWorkspace } from 'utils/hooks/useRememberWorkspace'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { MAX_WORKSPACE_NAME_LENGTH } from 'constants/workspaces'

export const WorkspaceCreationController: FC = () => {
  const { t } = useTranslation(['workspace_creation'])
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { baseUserData, currentUserWorkspace, organizationData, setCurrentUserWorkspace } = useUserData()
  const createWorkspace = useCreateWorkspace()
  const rememberWorkspace = useRememberWorkspace()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const [workspaceName, setWorkspaceName] = useState('')

  /**
   * Set by the pricing redirect for a user who cannot buy a plan in the workspace they are in
   * (QA-246). Without it this page reads as the wrong destination rather than the answer to the
   * subscribe intent they arrived with.
   */
  const missingPlanPermission = !!(location.state as { missingPlanPermission?: boolean } | null)?.missingPlanPermission

  /**
   * Log user entering this page - mirrors the 'Enter Subscription Flow' event the legacy PlanCreation
   * step fired on mount, so the funnel entry stays continuous now that this page replaces it (ISSUE-1716)
   */
  const enterLogged = useRef<boolean>(false)
  useEffect(() => {
    if (!enterLogged.current) {
      logAnalyticsEvent('Enter Subscription Flow', {
        userEmail: baseUserData?.email,
        organizationId: organizationData?.id,
        planType: SubscriptionPlan.PRO_PER_SEAT
      })

      enterLogged.current = true
    }
  }, [baseUserData?.email, organizationData?.id])

  const isFormValid = !!workspaceName.trim() && workspaceName.length <= MAX_WORKSPACE_NAME_LENGTH

  /**
   * Holds the page in its loading state between the creation succeeding and the new workspace
   * becoming resolvable, so no exit — submit, close, anything — can outrun the selection
   * (see handleSubmit).
   */
  const [isAwaitingWorkspaceSwitch, setIsAwaitingWorkspaceSwitch] = useState(false)

  /** The page can unmount during the wait (browser back); navigating then would yank the user back. */
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!isFormValid || createWorkspace.isPending || isAwaitingWorkspaceSwitch) return

    let workspaceId: string
    try {
      const result = await createWorkspace.mutateAsync({ workspaceName: workspaceName.trim() })
      workspaceId = result.id
    } catch (error: any) {
      logger.error('Workspace creation failed', error, { context: { component: 'WorkspaceCreationController', action: 'createWorkspace' } })
      const message = error?.response?.data?.message
      spawnErrorToast(message || t('error_toast'))
      return
    }

    // Creating a workspace is an explicit choice to work in it, so it is remembered like a switch —
    // the creator is already an admin member, so the preference write is accepted.
    sessionStorage.setItem('workspaceId', workspaceId)
    rememberWorkspace(workspaceId)
    logAnalyticsEvent(AnalyticsEvent.WORKSPACE_CREATED, { workspaceId, workspaceName: workspaceName.trim() })
    spawnSuccessToast(t('success_toast'))

    // Navigation waits until userMe can resolve the new workspace (ISSUE-2896). Navigating right
    // after a fire-and-forget invalidation landed the Plan page while the PREVIOUS workspace was
    // still selected — the stored id only wins once a userMe snapshot contains the workspace — so
    // the plan shown, and a checkout started from it, could target the wrong workspace. Worse, a
    // snapshot from before the creation landing in that window made the resolution fall back and
    // overwrite the stored id, sticking the wrong selection permanently. The awaited refetch
    // replaces any stale in-flight request (react-query cancels it), the poll covers propagation
    // lag and resolves with no extra delay when the refetch already carried the membership. Every
    // path past creation navigates — after an exhausted poll (logged by the helper) the pre-fix
    // behaviour is the worst case.
    setIsAwaitingWorkspaceSwitch(true)
    let resolved = false
    try {
      await queryClient.refetchQueries({ queryKey: [EntityKeys.USER_ME, QueryType.GET], exact: true })
      resolved = await pollUserMeUntilWorkspaceMember(queryClient, workspaceId)
    } catch (error: any) {
      // Effectively unreachable — refetchQueries swallows query errors and the poll never rejects —
      // but an unhandled rejection here would skip the navigation and strand the user on this page.
      logger.error('Waiting for the created workspace to resolve failed', error, { context: { component: 'WorkspaceCreationController', action: 'awaitWorkspaceSwitch' } })
    } finally {
      setIsAwaitingWorkspaceSwitch(false)
    }

    if (resolved) {
      // Commit the selection here instead of trusting the resolution effect's storage read: any userMe
      // snapshot re-render during the wait (an Auth0 token renew changes the roles identity and re-fires
      // the effect) could have fallen back and overwritten the stored id. Setting the selection is what
      // the workspace switcher does, and the sync effect re-writes sessionStorage from it. The membership
      // is read from the query cache, not getWorkspaceItemById — this callback's closure predates the
      // refetch, so the context helper still sees the pre-creation workspace arrays.
      const createdWorkspace = getWorkspaceMembershipFromCache(queryClient, workspaceId)
      if (createdWorkspace) setCurrentUserWorkspace(createdWorkspace)
    } else {
      // The one line that distinguishes this billing-adjacent gate from the helper's generic WARN: the
      // user is about to land on a Plan page that may still target the previous workspace. Reached on
      // poll exhaustion but also on a failed or offline refetch — refetchQueries resolves successfully
      // in both of those cases (errors are swallowed, a paused query resolves immediately), so this,
      // not a catch, is where every silent failure of the gate surfaces.
      logger.error('Created workspace did not resolve before navigation; the Plan page may target the previous workspace', undefined, {
        context: { component: 'WorkspaceCreationController', action: 'awaitWorkspaceSwitch', metadata: { workspaceId } },
      })
    }
    // Deliberate on failure too: stranding the user on the creation page would be strictly worse, and
    // the conditions that fail the gate (offline, dead session) fail a checkout as well.
    if (isMountedRef.current) navigate(Path.WORKSPACE_SETTINGS_PLAN)
  }, [isFormValid, createWorkspace, isAwaitingWorkspaceSwitch, workspaceName, queryClient, spawnSuccessToast, spawnErrorToast, t, navigate, rememberWorkspace, setCurrentUserWorkspace])

  // Closing without creating anything leaves the plan permissions as they were, so for the user the
  // banner is addressed to, the usual Plan page is the one place they still cannot go — it would
  // bounce them to the dashboard unexplained, the exact landing this redirect exists to avoid.
  const handleClose = useCallback(() => {
    navigate(missingPlanPermission ? Path.INDEX : Path.WORKSPACE_SETTINGS_PLAN)
  }, [navigate, missingPlanPermission])

  return (
    <ActionPage
      id="workspace-creation"
      onClose={handleClose}
      // The close button navigates to the same Plan page the gate below protects, so it must be held
      // during the wait too — otherwise X during the spinner reproduces the bug the gate closes.
      disabled={createWorkspace.isPending || isAwaitingWorkspaceSwitch}
      customNavigationTab={
        <Stack direction="row" alignItems="center">
          <Logo type='black' width="11rem" flexShrink={0} />
          <MUIDivider margin={12} orientation="vertical" sx={{ height: '2.4rem' }} />
          <Typography variant="text-md" fontWeight={550} color={GRAY_900}>
            {workspaceName || currentUserWorkspace?.name}
          </Typography>
        </Stack>
      }
    >
      <Stack
        flex={1}
        alignItems="center"
        overflow="auto"
        paddingTop="8rem"
        paddingBottom="4rem"
      >
        <Stack width="36rem" gap="1.6rem" alignItems="center">

          {/* Why the pricing link landed here instead of on a plan */}
          {missingPlanPermission && (
            <SystemMessage
              variant="warning"
              message={t('missing_plan_permission')}
              sx={{ marginBottom: '0.8rem' }}
            />
          )}

          {/* Title */}
          <Stack gap="1.2rem" alignItems="center" marginBottom="2.4rem">
            <Typography
              variant="h6"
              color={GRAY_900}
              fontWeight={400}
              fontFamily={RhetorikFontFamily}
              fontSize="2.4rem"
            >
              {t('title')}
            </Typography>
            <Typography variant="text-sm" color={GRAY_700}>
              {t('subtitle')}
            </Typography>
          </Stack>

          {/* Workspace name */}
          <MUIInputField
            label={t('workspace_name')}
            value={workspaceName}
            onChange={e => setWorkspaceName(e.target.value)}
            inputBoxWidth="36rem"
            maxLength={MAX_WORKSPACE_NAME_LENGTH}
          />

          {/* Warning message */}
          <SystemMessage
            variant="info"
            message={t('creation_warning')}
          />

          {/* Submit button */}
          <MUIButton
            size="lg"
            endIcon={<ArrowForwardRoundedIcon fontSize="large" />}
            disabled={!isFormValid || createWorkspace.isPending || isAwaitingWorkspaceSwitch}
            isLoading={createWorkspace.isPending || isAwaitingWorkspaceSwitch}
            onClick={handleSubmit}
          >
            {t('create_button')}
          </MUIButton>

        </Stack>
      </Stack>
    </ActionPage>
  )
}
